/* global BigInt */
import React, { useEffect, useState, useRef } from "react";
import { getAddChainParams } from '../utils/chainParams';
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { ethers } from "ethers";
import { getWCProvider, bringWalletToFront, getLastWallet, WC_WALLETS, pingWCSession, purgeWCSession } from "../utils/walletConnect";
import walletIcon from '../assets/icon/wallet.png'
import checkIcon from '../assets/icon/checked.png'
import getIcon from "../utils/Icon";
import userApi from "../api/userApi";
import blockchainApi from "../api/blockchainApi";
import ConnectWallet from "../components/ConnectWallet/ConnectWallet";
import TxProgressOverlay from "../components/TxProgressOverlay/TxProgressOverlay";
import Avatar from "../components/avatar/Avatar";
import CoinBadge from "../components/coin_badge/CoinBadge";
import usersIcon from '../assets/icon/users.png'
import cupIcon from '../assets/icon/cup.png'
import tokenApi from "../api/tokenApi";
import CrystalsApi from "../api/CrystalsApi";
import feeApi from "../api/feeApi";
import PromosApi from "../api/PromosApi";

const RPC_PROXY = 'https://api.duotetra.quest/promos/blockchain/rpc';

const ERC20_ABI = [
    'function approve(address spender, uint256 amount) returns (bool)',
    'function allowance(address owner, address spender) view returns (uint256)',
    'function balanceOf(address account) view returns (uint256)',
    'function decimals() view returns (uint8)',
];

const GIVEAWAY_ABI = [
    'function deposit(uint256 promoId, address tokenAddress, uint256 amount, uint256 endTime, bytes signature)',
];

const FEE_ABI = [
    'function payFee(uint256 promoId, uint256 amount)',
    'function feeToken() view returns (address)',
];

const short = (addr) => addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : '';

export default function DepositLayout() {
    const promo = useSelector((state) => state.selectedPromo?.promo);
    const [token, setToken] = useState(null);
    const [walletAddress, setWalletAddress]   = useState('');
    const [walletSheetOpen, setWalletSheetOpen] = useState(false);
    const [payerApp, setPayerApp] = useState(null); // {key, name} активного кошелька
    const [blockchainConfig, setBlockchainConfig] = useState(null);
    const [depositLoading, setDepositLoading] = useState(false);
    const [payLoading, setPayLoading] = useState(false);
    const [depositDone, setDepositDone] = useState(false);
    const [feeDone, setFeeDone] = useState(false);
    // { type: 'deposit'|'fee', step: 'connecting'|'approve_sign'|'approve_wait'|'tx_sign'|'tx_wait'|'done', error?: string } | null
    const [txStatus, setTxStatus] = useState(null);
    const lastTxActionRef = useRef(null);
    const [crystal, setCrystal] = useState([]);
    const [fee, setFee] = useState(null);
    const [userPromos, setUserPromos] = useState([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedPromoIndex, setSelectedPromoIndex] = useState(null); // null = MyBalance, 0+ = промо индекс
    
    // Токены и кристаллы для каждого промо в dropdown (по promoId)
    const [promoTokens, setPromoTokens] = useState({}); // { promoId: tokenData }
    const [promoCrystals, setPromoCrystals] = useState({}); // { promoId: crystalData }
    
    // State для MyBalance
    const [tokenAddress, setTokenAddress] = useState('');
    const [selectedBlockchain, setSelectedBlockchain] = useState('ETH');
    const [foundBalanceToken, setFoundBalanceToken] = useState(null);
    const [depositAmount, setDepositAmount] = useState('');
    const [isSearchingToken, setIsSearchingToken] = useState(false);
    
    // Список блокчейнов
    const blockChainsList = [
        { key: 'ETH', name: 'ETH', fullName: 'Ethereum' },
        { key: 'BNB', name: 'BSC', fullName: 'Binance' },
        { key: 'solana', name: 'SOL', fullName: 'Solana' },
        { key: 'sui', name: 'SUI', fullName: 'Sui' },
        { key: 'base', name: 'BASE', fullName: 'Base' },
        { key: 'TON', name: 'TON', fullName: 'TON' },
        { key: 'polygon_chain', name: 'MATIC', fullName: 'Polygon' },
        { key: 'optimism', name: 'OP', fullName: 'Optimism' },
        { key: 'arbitrum', name: 'ARB', fullName: 'Arbitrum' },
        { key: 'avalanche', name: 'AVAX', fullName: 'Avalanche' }
    ];
    
    // Адрес, с которого РЕАЛЬНО уйдут транзакции — это кошелёк с живой
    // WC-сессией. Primary из базы — только фолбэк для отображения.
    const refreshPayer = async () => {
        const w = WC_WALLETS.find(x => x.key === getLastWallet());
        setPayerApp(w ? { key: w.key, name: w.name } : null);
        try {
            const wc = await getWCProvider();
            const live = wc?.session && wc.accounts?.[0];
            if (live) { setWalletAddress(live); return; }
        } catch {}
        try {
            const wallets = await userApi.getWallets();
            const primary = wallets?.find(w => w.isPrimary) || wallets?.[0];
            setWalletAddress(primary?.walletAddress || '');
        } catch {
            try {
                const user = await userApi.getMe();
                setWalletAddress(user?.walletAddress || '');
            } catch {}
        }
    };

    // Загружаем плательщика и blockchain config
    useEffect(() => {
        refreshPayer();
        blockchainApi.getConfig().then(res => {
            setBlockchainConfig(res.data || res);
        }).catch(() => {});
    }, []);

    // Загружаем blockchain статус промо при монтировании
    useEffect(() => {
        if (!promo?.id) return;
        blockchainApi.getPromoBlockchainInfo(promo.id).then(res => {
            const info = res?.data || res;
            // blockchainStatus values: 'funded', 'pending_fee', 'pending_deposit' (lowercase)
            const bs = info?.blockchainStatus?.toLowerCase();
            if (bs === 'funded') {
                setDepositDone(true);
                setFeeDone(true);
            } else {
                if (info?.depositTxHash) setDepositDone(true);
                if (info?.feeTxHash) setFeeDone(true);
            }
        }).catch(() => {});
    }, [promo?.id]);

    // Флаг: пришли из Balance (promo был null при монтировании)
    // Используем useRef чтобы сохранить значение между рендерами
    const initialPromoRef = useRef(promo);
    const cameFromBalance = initialPromoRef.current === null;
    
    // При монтировании загружаем промо пользователя если пришли из Balance
    useEffect(() => {
        if (cameFromBalance) {
            loadUserPromos();
        }
    }, []);

    // Определяем, показывать ли dropdown
    // Dropdown показывается только если пришли из Balance
    const showDropdown = cameFromBalance && userPromos.length > 0;
    
    // Текущее промо для отображения
    // Если пришли из Balance - берем из selectedPromoIndex, иначе из Redux
    const currentPromo = cameFromBalance 
        ? (selectedPromoIndex !== null && userPromos[selectedPromoIndex] ? userPromos[selectedPromoIndex] : null)
        : promo;

    // промоутер (первый из массива)
    const promoter = currentPromo?.promoters?.[0];
    const fullPromoterName = promoter?.name || "Unknown";
    // Ограничиваем имя до 12 символов
    const promoterName = fullPromoterName.length > 10 ? fullPromoterName.slice(0, 10) + '...' : fullPromoterName;
    const promoterLogo = promoter?.logo || '';

    const getToken = async () => {
        if (!currentPromo?.tokenType) return;
        try {
            const response = await tokenApi.lookupToken(currentPromo.tokenType, 't');
            setToken(response); // Сохраняем весь объект токена
        } catch (err) {
            console.error('lookupToken error:', err);
        }
    };

    const getCrystal = async () => {
        if (!currentPromo?.category?.positions?.length) return;
        try {
            const result = await CrystalsApi.getGemsByPositions([currentPromo.category.positions.length]);
            setCrystal(result);
        } catch (err) {
            console.error('cant find gem:', err);
        }
    };

    const calculateFee = async () => {
        if (!currentPromo?.id) return;
        try {
            const participantsCount =
                currentPromo?.participantsCount || currentPromo?.maxParticipantsCount || 0;
            const isGem = currentPromo?.promoType === 'onlyCrystals';
            const result = isGem
                ? await feeApi.calculateFeeGem(currentPromo.id, participantsCount)
                : await feeApi.calculateFee(currentPromo.id, participantsCount);
            setFee(result);
            console.log('calculateFee result:', result, 'promoType:', currentPromo?.promoType);
        } catch (err) {
            console.error('calculateFee error:', err);
        }
    };


    const loadUserPromos = async () => {
        try {
            const response = await PromosApi.UserPromos.myPromos();
            const myList = response?.data?.data;
            const safeMyList = Array.isArray(myList) ? myList : [];
            setUserPromos(safeMyList);
            
            // Загружаем токены и кристаллы для каждого промо
            const tokensMap = {};
            const crystalsMap = {};
            
            await Promise.all(safeMyList.map(async (p) => {
                // Загружаем токен
                if (p?.tokenType) {
                    try {
                        const tokenData = await tokenApi.lookupToken(p.tokenType, 't');
                        tokensMap[p.id] = tokenData;
                    } catch (err) {
                        console.error(`Token lookup error for promo ${p.id}:`, err);
                    }
                }
                
                // Загружаем кристалл
                if (p?.category?.positions?.length) {
                    try {
                        const crystalData = await CrystalsApi.getGemsByPositions([p.category.positions.length]);
                        crystalsMap[p.id] = crystalData;
                    } catch (err) {
                        console.error(`Crystal lookup error for promo ${p.id}:`, err);
                    }
                }
            }));
            
            setPromoTokens(tokensMap);
            setPromoCrystals(crystalsMap);
        } catch (err) {
            console.error('Failed to load user promos:', err);
            setUserPromos([]);
        }
    };

    useEffect(() => {
        // Загружаем данные промо
        if (currentPromo) {
            getToken();
            getCrystal();
            calculateFee();
        } else {
            // Если нет промо (MyBalance или пустой выбор), очищаем данные
            setToken(null);
            setCrystal([]);
            setFee(null);
        }
    }, [currentPromo]);

    // Read-only провайдер — всегда Hardhat через RPC proxy
    const getReadProvider = () => new ethers.JsonRpcProvider(RPC_PROXY);

    // Получаем signer из MetaMask или WalletConnect (с таймаутом 15 сек)
    // Адрес кошелька: сначала из базы (подгружается при монтировании), иначе —
    // из ЖИВОЙ сессии кошелька. Раньше проверялось только значение из базы:
    // если кошелёк подключили уже после открытия экрана (или запись в базу
    // не прошла), аппка писала «Connect a wallet first» при реально
    // подключённом кошельке.
    const resolveWalletAddress = async () => {
        if (walletAddress) return walletAddress;
        try {
            const s = await getSigner();
            const addr = await s.getAddress();
            if (addr) setWalletAddress(addr);
            return addr || '';
        } catch {
            return '';
        }
    };

    const getSigner = async () => {
        const withTimeout = (promise, ms, msg) =>
            Promise.race([promise, new Promise((_, rej) => setTimeout(() => rej(new Error(msg)), ms))]);

        // 1. MetaMask / инжектированный провайдер
        if (window.ethereum) {
            const provider = new ethers.BrowserProvider(window.ethereum);
            await withTimeout(
                provider.send('eth_requestAccounts', []),
                15000, 'MetaMask не ответил. Разблокируй MetaMask и попробуй снова.'
            );
            return provider.getSigner();
        }

        // 2. WalletConnect — уже активная сессия
        const wc = await withTimeout(
            getWCProvider(),
            10000, 'Не удалось инициализировать WalletConnect'
        );

        if (wc && (wc.connected || wc.session)) {
            // Сессия может быть мертва на стороне кошелька (он её забыл), а мы
            // не в курсе — тогда запросы подписи улетают в пустоту. Пингуем:
            // явное «мертва» → просим переподключить; таймаут (кошелёк спит) — ок.
            const alive = await pingWCSession(undefined, 2000);
            if (alive === false) {
                try { await purgeWCSession(); } catch {}
                throw new Error('Wallet session expired. Reconnect your wallet (From → Change).');
            }
            const provider = new ethers.BrowserProvider(wc);
            return provider.getSigner();
        }

        // Сессии нет (например, вебвью перезагрузился после возврата из кошелька).
        // Не отправляем юзера вручную на страницу Wallet — пробуем восстановить
        // подключение прямо здесь: он подтвердит в кошельке и действие продолжится.
        try {
            await withTimeout(wc.connect(), 180000, 'Кошелёк не ответил на запрос подключения');
            if (wc.connected || wc.session) {
                return new ethers.BrowserProvider(wc).getSigner();
            }
        } catch (e) {
            if (e?.message?.includes('не ответил')) throw e;
        }

        throw new Error('Кошелёк не подключён. Зайди на страницу Wallet и подключи кошелёк.');
    };

    // Переключаем сеть — не блокируем если не поддерживается
    const switchChain = async (provider, chainId) => {
        const hexChainId = '0x' + chainId.toString(16);
        try {
            await Promise.race([
                provider.send('wallet_switchEthereumChain', [{ chainId: hexChainId }]),
                new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 8000)),
            ]);
        } catch (err) {
            if (err?.message === 'timeout') return; // не блокируем
            if (err?.code === 4902 || err?.message?.includes('Unrecognized chain') || err?.message?.includes('wallet_addEthereumChain')) {
                // Параметры берём по chainId, а не хардкодим Hardhat — иначе на
                // мейннете кошелёк добавил бы Arbitrum под именем локальной сети.
                const addParams = getAddChainParams(chainId, RPC_PROXY);
                if (addParams) {
                    try {
                        await provider.send('wallet_addEthereumChain', [addParams]);
                    } catch (_) { /* игнорируем — попробуем отправить транзакцию всё равно */ }
                }
            }
            // Остальные ошибки игнорируем — wallet может уже быть на нужной сети
        }
    };

    // ethers v6 заворачивает нестандартные ответы кошелька в «could not coalesce
    // error» — настоящая причина лежит глубже, в info.error. Достаём её.
    const txErrorMessage = (err, fallback) => {
        // Ответ бэкенда (axios): в data.message лежит человеческая причина
        const api = err?.response?.data?.message;
        if (api) return Array.isArray(api) ? api.join(', ') : String(api);
        const deep = err?.info?.error?.message || err?.error?.message;
        if (deep) return deep;
        const msg = err?.shortMessage || err?.reason || err?.message;
        if (msg && !/could not coalesce/i.test(msg)) return msg;
        return msg ? `${fallback} (wallet returned a malformed error)` : fallback;
    };

    // Deposit: ERC-20 approve → GiveawayPool.deposit()
    const handleDeposit = async () => {
        if (!currentPromo) return;
        const activeAddress = await resolveWalletAddress();
        if (!activeAddress) {
            setTxStatus({ type: 'deposit', step: 'connecting', error: 'Connect a wallet first' });
            return;
        }
        lastTxActionRef.current = handleDeposit;
        setDepositLoading(true);
        setTxStatus({ type: 'deposit', step: 'connecting' });
        let senderAddress = '';
        try {
            // Сверку «депозит уже прошёл?» (мог быть подписан из старого запроса
            // в очереди кошелька) гоним ПАРАЛЛЕЛЬНО с подключением — каждая лишняя
            // секунда до открытия кошелька снижает шанс, что Telegram выполнит
            // автопереход (openLink привязан к недавнему тапу).
            const [signer, infoRes] = await Promise.all([
                getSigner(),
                blockchainApi.getPromoBlockchainInfo(currentPromo.id).catch(() => null),
            ]);
            const promoInfo = infoRes?.data || infoRes;
            if (promoInfo?.depositTxHash) {
                setDepositDone(true);
                setTxStatus({ type: 'deposit', step: 'done' });
                return;
            }
            const cfg = blockchainConfig;
            if (!cfg?.giveawayPool) throw new Error('Blockchain config not loaded');

            const walletProvider = signer.provider;
            await switchChain(walletProvider, cfg.chainId || 31337);

            // Кошелёк мог молча проигнорировать переключение сети (switchChain
            // глотает отказ) — сверяем фактическую сеть, иначе approve уходит
            // не туда и падает невнятным «execution reverted»
            const actualChainHex = await walletProvider.send('eth_chainId', []);
            if (cfg.chainId && Number(actualChainHex) !== Number(cfg.chainId)) {
                throw new Error(`Wrong network: switch your wallet to Arbitrum One (now on chain ${Number(actualChainHex)})`);
            }

            // В базе адрес бывает со сбитым регистром (EIP-55 не сходится) —
            // нормализуем, иначе ethers.Contract его отвергнет
            const tokenAddress = ethers.getAddress(
                String(currentPromo.tokenContractAddress || currentPromo.tokenType || '').toLowerCase()
            );

            // Подпись просим для адреса, который РЕАЛЬНО отправит транзакцию
            // (контракт сверяет её с msg.sender). Сохранённый в базе адрес может
            // быть другим кошельком юзера — тогда контракт отбил бы
            // «Invalid deposit signature».
            senderAddress = await signer.getAddress();

            // Разрешение на депозит от бэкенда (сумму и endTime задаёт сервер —
            // используем ИМЕННО их) и текущий allowance читаем параллельно.
            const [permit, currentAllowance] = await Promise.all([
                blockchainApi.signDeposit({
                    promoId: currentPromo.id,
                    promoterAddress: senderAddress,
                }),
                (async () => {
                    try {
                        const readToken = new ethers.Contract(tokenAddress, ERC20_ABI, getReadProvider());
                        return BigInt(await readToken.allowance(senderAddress, cfg.giveawayPool));
                    } catch { return 0n; }
                })(),
            ]);
            const depositAmount = BigInt(permit.amount);

            // Если разрешение уже выдано (повторная попытка после сбоя) —
            // approve пропускаем: меньше подписей, и нет второго approve,
            // на котором флоу зависал.
            const alreadyApproved = currentAllowance >= depositAmount;

            if (!alreadyApproved) {
                setTxStatus({ type: 'deposit', step: 'approve_sign' });
                const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
                const approveTxReq = tokenContract.approve(cfg.giveawayPool, depositAmount);
                setTimeout(bringWalletToFront, 700);
                const approveTx = await approveTxReq;
                setTxStatus({ type: 'deposit', step: 'approve_wait' });
                await approveTx.wait();
            }

            setTxStatus({ type: 'deposit', step: 'tx_sign' });
            const pool = new ethers.Contract(cfg.giveawayPool, GIVEAWAY_ABI, signer);
            const depositTxReq = pool.deposit(
                currentPromo.id, tokenAddress, depositAmount, permit.endTime, permit.signature,
            );
            setTimeout(bringWalletToFront, 700);
            const depositTx = await depositTxReq;
            setTxStatus({ type: 'deposit', step: 'tx_wait' });
            const receipt = await depositTx.wait();

            // Уведомляем бэкенд
            await blockchainApi.notifyDeposited({
                promoId: currentPromo.id,
                promoter: senderAddress,
                token: tokenAddress,
                amount: depositAmount.toString(),
                txHash: receipt.hash,
            });
            setDepositDone(true);
            setTxStatus({ type: 'deposit', step: 'done' });
        } catch (err) {
            console.error('Deposit error:', err);
            const from = senderAddress ? ` · from …${senderAddress.slice(-6)}` : '';
            setTxStatus(prev => ({
                type: 'deposit',
                step: prev?.step || 'connecting',
                error: `#${currentPromo?.id ?? '?'} · step:${prev?.step || '?'}${from} · ${txErrorMessage(err, 'Deposit failed')}`,
            }));
        } finally {
            setDepositLoading(false);
        }
    };

    // Pay fee: ERC-20 approve → FeeCollector.payFee()
    const handlePayFee = async () => {
        if (!currentPromo) return;
        const activeAddress = await resolveWalletAddress();
        if (!activeAddress) {
            setTxStatus({ type: 'fee', step: 'connecting', error: 'Connect a wallet first' });
            return;
        }
        lastTxActionRef.current = handlePayFee;
        setPayLoading(true);
        setTxStatus({ type: 'fee', step: 'connecting' });
        try {
            // Сверка «комиссия уже оплачена?» — параллельно с подключением,
            // чтобы не отодвигать автопереход в кошелёк от тапа
            const [signer, feeInfoRes] = await Promise.all([
                getSigner(),
                blockchainApi.getPromoBlockchainInfo(currentPromo.id).catch(() => null),
            ]);
            const feePromoInfo = feeInfoRes?.data || feeInfoRes;
            if (feePromoInfo?.feeTxHash) {
                setFeeDone(true);
                setTxStatus({ type: 'fee', step: 'done' });
                return;
            }
            const cfg = blockchainConfig;
            if (!cfg?.feeCollector) throw new Error('Blockchain config not loaded');

            // Переключаем кошелёк на нужную сеть
            const walletProvider = signer.provider;
            await switchChain(walletProvider, cfg.chainId || 31337);

            // Сверяем фактическую сеть — кошелёк мог проигнорировать переключение
            const actualChainHex = await walletProvider.send('eth_chainId', []);
            if (cfg.chainId && Number(actualChainHex) !== Number(cfg.chainId)) {
                throw new Error(`Wrong network: switch your wallet to Arbitrum One (now on chain ${Number(actualChainHex)})`);
            }

            // Токен комиссии: на мейннете — feeToken (USDT0) из конфига бэкенда,
            // на деве — mock-USDT. Раньше брали только mock: на мейннете он пустой,
            // и ethers падал с «ENS name ... must be correctly configured».
            const feeTokenAddr = cfg.feeToken || cfg.mockUsdtAddress || cfg.mockUSDT;
            if (!feeTokenAddr) throw new Error('Fee token address is not configured');
            const feeAmount = fee !== null
                ? (typeof fee === 'number' ? fee : fee?.amount || fee?.fee || 0)
                : 0;

            // decimals и адрес отправителя — параллельно, через RPC proxy
            const readProvider = getReadProvider();
            const readFeeToken = new ethers.Contract(feeTokenAddr, ERC20_ABI, readProvider);
            const [decimals, feeSender] = await Promise.all([
                readFeeToken.decimals(),
                signer.getAddress(),
            ]);
            const rawFee = ethers.parseUnits(String(feeAmount), decimals);

            // Approve пропускаем, если разрешение уже есть с прошлой попытки
            let feeApproved = false;
            try {
                const allowance = await readFeeToken.allowance(feeSender, cfg.feeCollector);
                feeApproved = BigInt(allowance) >= rawFee;
            } catch {}

            if (!feeApproved) {
                setTxStatus({ type: 'fee', step: 'approve_sign' });
                const feeTokenContract = new ethers.Contract(feeTokenAddr, ERC20_ABI, signer);
                const feeApproveReq = feeTokenContract.approve(cfg.feeCollector, rawFee);
                setTimeout(bringWalletToFront, 700);
                const approveTx = await feeApproveReq;
                setTxStatus({ type: 'fee', step: 'approve_wait' });
                await approveTx.wait();
            }

            setTxStatus({ type: 'fee', step: 'tx_sign' });
            const feeCollector = new ethers.Contract(cfg.feeCollector, FEE_ABI, signer);
            const payReq = feeCollector.payFee(currentPromo.id, rawFee);
            setTimeout(bringWalletToFront, 700);
            const payTx = await payReq;
            setTxStatus({ type: 'fee', step: 'tx_wait' });
            const receipt = await payTx.wait();

            // Уведомляем бэкенд
            await blockchainApi.notifyFeePaid({
                promoId: currentPromo.id,
                payer: activeAddress,
                amount: rawFee.toString(),
                txHash: receipt.hash,
            });
            setFeeDone(true);
            setTxStatus({ type: 'fee', step: 'done' });
        } catch (err) {
            console.error('PayFee error:', err);
            const msg = `#${currentPromo?.id ?? '?'} · ${txErrorMessage(err, 'Fee payment failed')}`;
            setTxStatus(prev => ({
                type: 'fee',
                step: prev?.step || 'connecting',
                error: msg,
            }));
        } finally {
            setPayLoading(false);
        }
    };

    // Функция поиска токена по адресу для MyBalance
    const fetchBalanceToken = async () => {
        if (!tokenAddress.trim()) return;
        
        setIsSearchingToken(true);
        try {
            const response = await tokenApi.lookupToken(tokenAddress.trim(), selectedBlockchain.toLowerCase());
            const tokenData = response.data || response;
            
            if (tokenData && tokenData.symbol) {
                setFoundBalanceToken(tokenData);
            } else {
                setFoundBalanceToken(null);
            }
        } catch (err) {
            console.error('Token lookup error:', err);
            setFoundBalanceToken(null);
        } finally {
            setIsSearchingToken(false);
        }
    };

    // Обработчик выбора из dropdown
    const handleSelectPromo = (index) => {
        setSelectedPromoIndex(index);
        // Сбрасываем данные MyBalance при смене выбора
        if (index !== null) {
            setFoundBalanceToken(null);
            setTokenAddress('');
            setDepositAmount('');
        }
        setIsDropdownOpen(false);
        // Не диспатчим в Redux - используем локальный state для dropdown
    };

    // Закрытие dropdown при клике вне его
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen && !event.target.closest('.dropdown-container')) {
                setIsDropdownOpen(false);
            }
        };
        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isDropdownOpen]);


    const ToPromo =()=>{
        // Создаем список для dropdown: MyBalance + промо пользователя
        const dropdownItems = [
            { type: 'balance', label: 'MyBalance' },
            ...userPromos.map((p, idx) => ({ type: 'promo', promo: p, index: idx }))
        ];

        // Высота одного элемента dropdown (78px)
        const itemHeight = 78;
        // Максимум видимых элементов (3)
        const maxVisible = 3;
        const dropdownHeight = Math.min(dropdownItems.length, maxVisible) * itemHeight;

        return <div className={'w-full flex flex-col bg-white/[8%] border border-white/10 p-[16px] gap-[16px] rounded-[8px]'}>
        <div className={'w-full h-[18px] flex flex-row items-center justify-between'}>
                <span className={'text-[14px] text-[#D8D8D8] font-semibold'}>To promo</span>
        </div>
        
        {showDropdown ? (
            <div className={'relative w-full dropdown-container'}>
                {/* Dropdown button */}
                <div 
                    className={'w-full h-[78px] flex flex-col p-[12px] gap-2 bg-white/[8%] rounded-[8px] cursor-pointer border border-white/20'}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                    {selectedPromoIndex === null ? (
                        // Показываем MyBalance
                        <div className={'w-full h-full flex flex-col justify-center'}>
                            <div className={'w-full flex flex-row items-center justify-between'}>
                                <div className={'flex flex-row items-center justify-start gap-[12px]'}>
                                    {/* Иконка кошелька с градиентным фоном */}
                                    <div className={'w-[40px] h-[40px] rounded-[12px] bg-gradient-to-br from-[#F7C520] to-[#E5A800] flex items-center justify-center shadow-lg'}>
                                        <img width={22} height={22} src={walletIcon} alt="wallet" className="brightness-0" />
                                    </div>
                                    <div className={'flex flex-col gap-[2px]'}>
                                        <span className={'text-white text-[15px] font-semibold whitespace-nowrap'}>My Balance</span>
                                        <span className={'text-[#959595] text-[11px]'}>Deposit from wallet</span>
                                    </div>
                                </div>
                                {/* Chevron down для dropdown */}
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="#959595" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                        </div>
                    ) : currentPromo ? (
                        // Показываем выбранное промо
                        <>
                            <div className={'w-full flex flex-row items-center justify-between'}>
                                <div className={'flex flex-row items-center justify-start gap-[10px]'}>
                                    <img 
                                        width={28} 
                                        height={28} 
                                        src={crystal[0]?.frontImage ? `https://api.duotetra.quest/uploads/gems/${crystal[0].frontImage}` : getIcon('diamond')} 
                                    />
                                    <Avatar 
                                        width={24} 
                                        height={24} 
                                        icon={promoterLogo ? `https://api.duotetra.quest${promoterLogo}` : getIcon('dog')}
                                    />
                                    <span className={'text-white text-[12px] whitespace-nowrap'}>{promoterName}</span>
                                </div>
                                <div className={'flex flex-row items-center gap-[8px]'}>
                                    <div className={'w-[87px] h-[28px] flex flex-row items-center'}>
                                        <CoinBadge 
                                            count={currentPromo?.currentAmount || currentPromo?.totalAmount || 0} 
                                            iconHeight={16} 
                                            iconWidth={16} 
                                            token={token?.logoPath ? `https://api.duotetra.quest/${token.logoPath}` : 'ETH'} 
                                        />
                                    </div>
                                    {/* Chevron down для dropdown */}
                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M5 7.5L10 12.5L15 7.5" stroke="#959595" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                            </div>
                            <div className={'w-full flex flex-row items-center justify-start gap-[12px]'}>
                                <div className={'flex flex-row items-center justify-start gap-[4px]'}>
                                    <img width={18} height={18} src={usersIcon} />
                                    <span className={'text-[#CFCFCF] text-[14px]'}>{currentPromo?.maxParticipantsCount || currentPromo?.participantsCount || 0}</span>
                                </div>
                                <div className={'flex flex-row items-center justify-start gap-[4px]'}>
                                    <img width={18} height={18} src={cupIcon} />
                                    <span className={'text-[#CFCFCF] text-[14px]'}>{currentPromo?.prizePlacesCount || 0}</span>
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>

                {/* Dropdown list */}
                {isDropdownOpen && (
                    <div 
                        className={'absolute top-full mt-2 w-full bg-[#1B1B1D] border border-white/20 rounded-[8px] overflow-hidden z-50'}
                        style={{ maxHeight: `${dropdownHeight}px`, overflowY: 'auto' }}
                    >
                        {dropdownItems.map((item, idx) => {
                            const isSelected = (item.type === 'balance' && selectedPromoIndex === null) || 
                                             (item.type === 'promo' && selectedPromoIndex === item.index);
                            
                            return (
                                <div
                                    key={idx}
                                    className={`w-full h-[78px] flex flex-col p-[12px] gap-2 cursor-pointer transition-colors ${
                                        isSelected ? 'bg-white/[12%]' : 'bg-white/[4%] hover:bg-white/[8%]'
                                    } ${idx !== dropdownItems.length - 1 ? 'border-b border-white/10' : ''}`}
                                    onClick={() => handleSelectPromo(item.type === 'balance' ? null : item.index)}
                                >
                                    {item.type === 'balance' ? (
                                        <div className={'w-full h-full flex flex-col justify-center'}>
                                            <div className={'w-full flex flex-row items-center justify-between'}>
                                                <div className={'flex flex-row items-center justify-start gap-[12px]'}>
                                                    {/* Иконка кошелька с градиентным фоном */}
                                                    <div className={'w-[40px] h-[40px] rounded-[12px] bg-gradient-to-br from-[#F7C520] to-[#E5A800] flex items-center justify-center shadow-lg'}>
                                                        <img width={22} height={22} src={walletIcon} alt="wallet" className="brightness-0" />
                                                    </div>
                                                    <div className={'flex flex-col gap-[2px]'}>
                                                        <span className={'text-white text-[15px] font-semibold whitespace-nowrap'}>My Balance</span>
                                                        <span className={'text-[#959595] text-[11px]'}>Deposit from wallet</span>
                                                    </div>
                                                </div>
                                                {/* Стрелка или индикатор */}
                                                <div className={'w-[24px] h-[24px] rounded-full bg-white/10 flex items-center justify-center'}>
                                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M4.5 2.5L8 6L4.5 9.5" stroke="#959595" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        (() => {
                                            // Получаем токен и кристалл для этого промо
                                            const itemToken = promoTokens[item.promo?.id];
                                            const itemCrystal = promoCrystals[item.promo?.id];
                                            const itemPromoter = item.promo?.promoters?.[0];
                                            const itemPromoterName = itemPromoter?.name 
                                                ? (itemPromoter.name.length > 12 ? itemPromoter.name.slice(0, 12) + '...' : itemPromoter.name) 
                                                : 'Unknown';
                                            
                                            return (
                                                <>
                                                    <div className={'w-full flex flex-row items-center justify-between'}>
                                                        <div className={'flex flex-row items-center justify-start gap-[10px]'}>
                                                            <img 
                                                                width={28} 
                                                                height={28} 
                                                                src={itemCrystal?.[0]?.frontImage 
                                                                    ? `https://api.duotetra.quest/uploads/gems/${itemCrystal[0].frontImage}` 
                                                                    : getIcon('diamond')} 
                                                            />
                                                            <Avatar 
                                                                width={24} 
                                                                height={24} 
                                                                icon={itemPromoter?.logo 
                                                                    ? `https://api.duotetra.quest${itemPromoter.logo}` 
                                                                    : getIcon('dog')}
                                                            />
                                                            <span className={'text-white text-[14px] whitespace-nowrap'}>
                                                                {itemPromoterName}
                                                            </span>
                                                        </div>
                                                        <div className={'w-[87px] h-[28px] flex flex-row items-center'}>
                                                            <CoinBadge 
                                                                count={item.promo?.currentAmount || item.promo?.totalAmount || 0} 
                                                                iconHeight={16} 
                                                                iconWidth={16} 
                                                                token={itemToken?.logoPath 
                                                                    ? `https://api.duotetra.quest/${itemToken.logoPath}` 
                                                                    : getIcon('ETH')} 
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className={'w-full flex flex-row items-center justify-start gap-[12px]'}>
                                                        <div className={'flex flex-row items-center justify-start gap-[4px]'}>
                                                            <img width={18} height={18} src={usersIcon} />
                                                            <span className={'text-[#CFCFCF] text-[14px]'}>{item.promo?.maxParticipantsCount || item.promo?.participantsCount || 0}</span>
                                                        </div>
                                                        <div className={'flex flex-row items-center justify-start gap-[4px]'}>
                                                            <img width={18} height={18} src={cupIcon} />
                                                            <span className={'text-[#CFCFCF] text-[14px]'}>{item.promo?.prizePlacesCount || 0}</span>
                                                        </div>
                                                    </div>
                                                </>
                                            );
                                        })()
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        ) : (
            // Обычный вид (когда есть selectedPromo)
            <div className={'w-full h-[78px] flex flex-col p-[12px] gap-2 bg-white/[8%] rounded-[8px] '}>
                <div className={'w-full flex flex-row items-center justify-between '}>

                    <div className="flex items-center justify-center text-[11px] font-bold px-2 py-1 rounded-full bg-[#D3D3D3] text-black ">
                        {fee !== null
                            ? `$${typeof fee === 'number' ? fee.toFixed(2) : (Number(fee?.amount ?? fee?.fee) || 0).toFixed(2)}`
                            : '...'}
                    </div>

                    <div className="min-w-24 h-[20px] px-2 border border-white rounded-full text-white text-[10px] flex items-center justify-center whitespace-nowrap">
                        {(() => {
                            switch (currentPromo?.promoType) {
                                case 'onlyCrystals':
                                    return 'Crystals';
                                case 'cryptoOnly':
                                    return 'Crypto';
                                case 'crystalsAndCrypto':
                                default:
                                    return 'Crystals and Crypto';
                            }
                        })()}
                    </div>

                    {/* <div className={'flex flex-row items-center justify-start gap-[10px]'}>
                        <img 
                            width={28} 
                            height={28} 
                            src={crystal[0]?.frontImage ? `https://api.duotetra.quest/uploads/gems/${crystal[0].frontImage}` : getIcon('diamond')} 
                        />
                        <Avatar 
                            width={24} 
                            height={24} 
                            icon={promoterLogo ? `https://api.duotetra.quest${promoterLogo}` : getIcon('dog')}
                        />
                        <span className={'text-white text-[14px] whitespace-nowrap'}>{promoterName}</span>
                    </div> */}

                    <div className={'max-w-[87px] h-[28px] flex flex-row items-center justify-end'}>
                        <CoinBadge 
                            count={currentPromo?.currentAmount || currentPromo?.totalAmount || 0} 
                            iconHeight={16} 
                            iconWidth={16} 
                            token={token?.logoPath ? `https://api.duotetra.quest/${token.logoPath}` : 'ETH'} 
                        />
                    </div>
                </div>
                <div className={'w-full flex flex-row items-center justify-between gap-[12px]'}>
                    <div className={'flex flex-row items-center justify-start gap-[10px]'}>
                        <img 
                            width={28} 
                            height={28} 
                            src={crystal[0]?.frontImage ? `https://api.duotetra.quest/uploads/gems/${crystal[0].frontImage}` : getIcon('diamond')} 
                        />
                        <Avatar 
                            width={24} 
                            height={24} 
                            icon={promoterLogo ? `https://api.duotetra.quest${promoterLogo}` : getIcon('dog')}
                        />
                        <span className={'text-white text-[14px] whitespace-nowrap'}>{promoterName}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className={'flex flex-row items-center justify-start gap-[4px]'}>
                                <img width={18} height={18} src={usersIcon} />
                                <span className={'text-[#CFCFCF] text-[14px]'}>{currentPromo?.maxParticipantsCount || currentPromo?.participantsCount || 0}</span>
                        </div>
                        <div className={'flex flex-row items-center justify-start gap-[4px]'}>
                                <img width={18} height={18} src={cupIcon} />
                                <span className={'text-[#CFCFCF] text-[14px]'}>{currentPromo?.prizePlacesCount || 0}</span>
                        </div>
                    </div>
                    
                </div>
            </div>
        )}
        <div className={'w-full h-[18px] flex flex-row items-center justify-between'}>
                <span className={'text-[14px] text-[#D8D8D8] font-semibold'}>Budget</span>
        </div>
        
        {/* Budget для MyBalance - выбор токена и количества в стиле C&C */}
        {showDropdown && selectedPromoIndex === null ? (
            <div className={'w-full flex flex-col gap-[12px]'}>
                {/* Blockchain и Token badges */}
                <div className={'w-full flex flex-row items-center gap-3'}>
                    <span className={'text-[12px] text-white font-medium'}>Blockchain</span>
                    <div className={'h-[26px] flex flex-row items-center justify-center border border-[#F7C520] gap-2 rounded-[4px] px-3'}>
                        <img
                            width={16}
                            height={16}
                            src={getIcon(selectedBlockchain)}
                            alt={blockChainsList.find(b => b.key === selectedBlockchain)?.name || 'Blockchain'}
                            className={'flex-shrink-0 object-contain'}
                        />
                        <span className={'text-[11px] text-white font-medium'}>
                            {blockChainsList.find(b => b.key === selectedBlockchain)?.fullName || 'Ethereum'}
                        </span>
                    </div>
                    <span className={'text-[12px] text-white font-medium'}>Token</span>
                    <div className={'h-[26px] flex flex-row items-center justify-center border border-[#F7C520] gap-2 rounded-[4px] px-3'}>
                        {foundBalanceToken ? (
                            <>
                                <img
                                    width={16}
                                    height={16}
                                    src={`https://api.duotetra.quest/${foundBalanceToken.logoPath}`}
                                    alt={foundBalanceToken.symbol}
                                    className={'flex-shrink-0 object-contain rounded-full'}
                                />
                                <span className={'text-[11px] text-white font-medium'}>{foundBalanceToken.symbol}</span>
                            </>
                        ) : (
                            <>
                                <img
                                    width={16}
                                    height={16}
                                    src={getIcon('img')}
                                    alt="Unknown"
                                    className={'flex-shrink-0 object-contain'}
                                />
                                <span className={'text-[11px] text-[#959595] font-medium'}>Unknown</span>
                            </>
                        )}
                    </div>
                </div>

                {/* Список блокчейнов в виде чипов */}
                <div className={'w-full flex flex-row flex-wrap items-center gap-2'}>
                    {blockChainsList.map((chain) => {
                        const isSelected = selectedBlockchain === chain.key;
                        return (
                            <div
                                key={chain.key}
                                onClick={() => {
                                    setSelectedBlockchain(chain.key);
                                    setFoundBalanceToken(null);
                                }}
                                className={`h-[24px] flex flex-row items-center justify-center rounded-[4px] border gap-1 px-2 cursor-pointer transition-all duration-200 ${
                                    isSelected
                                        ? 'border-white bg-white/10'
                                        : 'border-[#353232] hover:border-white/50'
                                }`}
                            >
                                <img
                                    width={14}
                                    height={14}
                                    src={getIcon(chain.key)}
                                    alt={chain.name}
                                    className={'flex-shrink-0 object-contain'}
                                />
                                <span className={'text-[10px] text-white whitespace-nowrap'}>
                                    {chain.name}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {/* Ввод адреса токена с кнопками */}
                <div className={'flex flex-row items-center gap-2'}>
                    <div className={'relative flex-1 h-[28px] rounded-[4px] border border-[#353232] bg-[#121212] hover:border-white/50 transition-colors'}>
                        <input
                            type="text"
                            value={tokenAddress}
                            onChange={(e) => setTokenAddress(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    fetchBalanceToken();
                                }
                            }}
                            placeholder="0x0...     token address"
                            className={'w-full h-full px-2 bg-transparent text-white text-[12px] focus:outline-none placeholder-[#959595]'}
                        />
                    </div>
                    {/* Clear button */}
                    <div
                        className={'w-[26px] h-[26px] bg-[#262626] border border-[#3B3B3B] rounded-[4px] flex items-center justify-center cursor-pointer hover:bg-[#353232] transition-colors'}
                        onClick={() => {
                            setTokenAddress('');
                            setFoundBalanceToken(null);
                        }}
                    >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 3L3 9M3 3L9 9" stroke="#959595" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    {/* Search button */}
                    <div
                        className={`w-[26px] h-[26px] bg-[#262626] border border-[#3B3B3B] rounded-[4px] flex items-center justify-center cursor-pointer hover:bg-[#353232] transition-colors ${isSearchingToken ? 'opacity-50' : ''}`}
                        onClick={() => {
                            if (!isSearchingToken && tokenAddress.trim()) {
                                fetchBalanceToken();
                            }
                        }}
                    >
                        {isSearchingToken ? (
                            <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        ) : (
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5.5 9.5C7.70914 9.5 9.5 7.70914 9.5 5.5C9.5 3.29086 7.70914 1.5 5.5 1.5C3.29086 1.5 1.5 3.29086 1.5 5.5C1.5 7.70914 3.29086 9.5 5.5 9.5Z" stroke="#959595" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M10.5 10.5L8.5 8.5" stroke="#959595" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        )}
                    </div>
                </div>

                {/* Найденный токен */}
                {foundBalanceToken && (
                    <div className={'w-full h-[40px] px-[12px] flex flex-row items-center justify-between bg-green-500/10 border border-green-400/30 rounded-[6px]'}>
                        <div className={'flex flex-row gap-2 items-center'}>
                            <img 
                                width={24} 
                                height={24} 
                                src={`https://api.duotetra.quest/${foundBalanceToken.logoPath}`}
                                className={'rounded-full'}
                            />
                            <div className={'flex flex-col'}>
                                <span className={'text-[13px] text-white font-medium'}>{foundBalanceToken.symbol}</span>
                                <span className={'text-[10px] text-[#959595]'}>{foundBalanceToken.name}</span>
                            </div>
                        </div>
                        <span className={'text-[11px] text-[#2FD159] font-medium'}>✓ Found</span>
                    </div>
                )}
                
                {/* Ввод количества */}
                <div className={'w-full h-[44px] px-[12px] py-[10px] flex flex-row items-center justify-between bg-white/[8%] rounded-[8px] border border-white/10'}>
                    <span className={'text-[14px] text-[#959595]'}>Amount:</span>
                    <input
                        type="text"
                        value={depositAmount}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Разрешаем только числа, точку и запятую
                            if (value === '' || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                // Заменяем запятую на точку для единообразия
                                const normalizedValue = value.replace(',', '.');
                                setDepositAmount(normalizedValue);
                            }
                        }}
                        placeholder="0.00"
                        className={'w-[150px] bg-transparent text-white text-[16px] font-semibold text-right focus:outline-none placeholder-[#959595]'}
                    />
                </div>
            </div>
        ) : (
            // Budget для промо
            <div className={'w-full flex flex-col gap-[10px]'}>
                <div className={'w-full h-[44px] px-[12px] py-[10px] flex flex-row items-center justify-between bg-white/[8%] rounded-[8px]'}>
                    <div className={'flex flex-row gap-2 items-center justify-start'}>
                        <img 
                            width={28} 
                            height={28} 
                            src={token?.logoPath ? `https://api.duotetra.quest/${token.logoPath}` : getIcon('ETH')} 
                        />
                        <span className={'text-[14px] text-[#D8D8D8]'}>{token?.symbol || token?.name || 'Ethereum'}</span>
                    </div>
                    <span className={'text-[16px] text-[#D5D5D5] font-semibold'}>
                        {currentPromo?.currentAmount || currentPromo?.totalAmount || 0}
                    </span>
                </div>
            </div>
        )}
        
        {/* Кнопка Deposit */}
        <div
            onClick={!depositLoading && !depositDone ? handleDeposit : undefined}
            className={`w-full flex items-center justify-center rounded-[8px] px-[24px] py-[12px] cursor-pointer transition-all min-h-[48px] ${
                depositDone ? 'bg-green-600' : 'bg-[#207FE0]'
            } ${depositLoading || depositDone ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
            {depositLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
                <span className={'text-white text-[15px] font-semibold'}>
                    {depositDone ? '✓ Deposited' : 'Deposit'}
                </span>
            )}
        </div>

        {/* Секция Cost - только для промо, НЕ для MyBalance */}
        {!(showDropdown && selectedPromoIndex === null) && (
        <div className={'w-full flex flex-col items-center gap-[10px]'}>
            <div className={'w-full h-[18px] flex flex-row items-center justify-between'}>
                <span className={'text-[14px] text-[#D8D8D8] font-semibold'}>Cost</span>
            </div>
            <div className={'w-full h-[44px] px-[12px] py-[10px] flex flex-row items-center justify-between bg-white/[8%] rounded-[8px]'}>
                <div className={'flex flex-row gap-2 items-center justify-start'}>
                    <img width={24} height={24} src={getIcon('USDT')} />
                    <span className={'text-[14px] text-[#D8D8D8]'}>USDT</span>
                </div>
                <span className={'text-[16px] text-[#D5D5D5] font-semibold'}>
                    {fee !== null ? (typeof fee === 'number' ? fee.toFixed(2) : fee?.amount || fee?.fee || '0.00') : '...'}
                </span>
            </div>
            <div
                onClick={!payLoading && !feeDone ? handlePayFee : undefined}
                className={`w-full h-[48px] flex flex-row items-center justify-center rounded-[8px] px-[24px] py-[14px] cursor-pointer transition-all ${
                    feeDone ? 'bg-green-600' : 'bg-[#207FE0]'
                } ${payLoading || feeDone ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
                {payLoading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                    <span className={'text-white text-[15px] font-semibold'}>
                        {feeDone ? '✓ Paid' : 'Pay'}
                    </span>
                )}
            </div>
        </div>
        )}
    </div>;
    };

    const From = () => (
        <div className="w-full flex flex-col gap-[10px] p-[16px] rounded-[8px] border border-white/10 bg-white/[8%]">
            <div className="w-full flex flex-row items-center justify-between">
                <span className="text-[14px] text-[#D8D8D8] font-semibold">From</span>
                {walletAddress && (
                    <button
                        onClick={() => setWalletSheetOpen(true)}
                        className="text-[11px] text-white/35 border border-white/10 rounded-full px-2.5 py-0.5 transition active:text-white/60"
                    >
                        Change
                    </button>
                )}
            </div>

            {walletAddress ? (
                /* Connected wallet row */
                <div className="w-full h-[44px] flex flex-row items-center justify-between px-[12px] py-[10px] border border-[#2FD159] rounded-[8px] bg-white/[8%]">
                    <img width={22} height={22} src={walletIcon} alt="wallet" />
                    <span className="text-[15px] text-[#D5D5D5] font-semibold font-mono">{short(walletAddress)}</span>
                    {payerApp && <span className="text-[11px] text-[#959595]">via {payerApp.name}</span>}
                    <img width={20} height={20} src={checkIcon} alt="connected" />
                </div>
            ) : (
                /* No wallet — connect button */
                <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setWalletSheetOpen(true)}
                    className="w-full h-[44px] flex flex-row items-center justify-between px-[12px] py-[10px] border border-dashed border-white/20 rounded-[8px] bg-white/[4%] transition active:bg-white/10"
                >
                    <div className="flex items-center gap-2">
                        <img width={20} height={20} src={walletIcon} alt="wallet" className="opacity-50" />
                        <span className="text-[14px] text-white/40 font-medium">Connect Wallet</span>
                    </div>
                    <svg className="text-white/25" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                    </svg>
                </motion.button>
            )}
        </div>
    );
    return (
        <div className={'w-full h-full flex flex-col px-[15px] py-[20px] gap-[10px] '}>
            <From/>
            <ToPromo/>

            {/* Wallet sheet — открывается из блока From */}
            <ConnectWallet
                open={walletSheetOpen}
                onClose={() => { setWalletSheetOpen(false); refreshPayer(); }}
                onConnected={() => refreshPayer()}
            />

            {/* Полноэкранный прогресс-оверлей для deposit / fee */}
            <TxProgressOverlay
                status={txStatus}
                onClose={() => setTxStatus(null)}
                onRetry={txStatus?.error && lastTxActionRef.current ? () => {
                    const fn = lastTxActionRef.current;
                    setTxStatus(null);
                    fn?.();
                } : undefined}
            />
        </div>
    );
}
